<template>
  <div class="terms-of-service">
    <div class="container mx-auto px-4 py-8">
      <div class="max-w-4xl mx-auto">
        <h1 class="text-4xl font-bold text-white mb-8">Terms of Service</h1>
        <div class="bg-gray-800 rounded-lg p-8 text-gray-300 space-y-6">
          
          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p>By accessing and using CyberGame Hub ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">2. Service Description</h2>
            <p>CyberGame Hub is an online gaming platform that provides access to various games through iframe embedding and direct links. We strive to offer a safe, enjoyable gaming experience for users worldwide.</p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">3. User Responsibilities</h2>
            <div class="space-y-4">
              <h3 class="text-lg font-medium text-blue-400 mb-2">Acceptable Use</h3>
              <p>You agree to use our service only for lawful purposes and in a way that does not infringe the rights of others or restrict their use and enjoyment of the service.</p>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Prohibited Activities</h3>
              <p>You must not:</p>
              <ul class="list-disc list-inside space-y-2 ml-4">
                <li>Upload, post, or transmit any content that is illegal, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable</li>
                <li>Impersonate any person or entity or falsely state or misrepresent your affiliation with a person or entity</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Use automated systems (bots, scrapers) to access our service</li>
                <li>Interfere with or disrupt the service or servers connected to the service</li>
                <li>Violate any applicable local, state, national, or international law</li>
                <li>Click on advertisements fraudulently or encourage others to do so</li>
                <li>Attempt to manipulate our advertising systems</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">4. Content Policy</h2>
            <div class="space-y-4">
              <h3 class="text-lg font-medium text-blue-400 mb-2">User-Generated Content</h3>
              <p>Users may submit comments, reviews, and other content. By submitting content, you grant us a non-exclusive, worldwide, royalty-free license to use, modify, and display such content.</p>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Content Moderation</h3>
              <p>We reserve the right to remove any content that violates these terms or is otherwise objectionable. We may also suspend or terminate accounts that repeatedly violate our content policies.</p>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Reporting Violations</h3>
              <p>If you encounter content that violates our policies, please report it to us at <a href="mailto:zhangshuaidoc@163.com" class="text-blue-400 hover:text-blue-300 underline">zhangshuaidoc@163.com</a>.</p>
            </div>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">5. Advertising and Third-Party Content</h2>
            <div class="space-y-4">
              <p>Our service displays advertisements through Google AdSense and may contain links to third-party websites and services.</p>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Advertisement Interaction</h3>
              <ul class="list-disc list-inside space-y-2 ml-4">
                <li>Do not click on advertisements fraudulently or repeatedly</li>
                <li>Do not encourage others to click on advertisements</li>
                <li>Do not use automated tools to interact with advertisements</li>
                <li>Report any inappropriate or malicious advertisements to us</li>
              </ul>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Third-Party Content</h3>
              <p>We are not responsible for the content, privacy policies, or practices of third-party websites or services. Your interactions with third-party content are solely between you and the third party.</p>
            </div>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">6. Privacy and Data Protection</h2>
            <p>Your privacy is important to us. Please review our <router-link to="/privacy" class="text-blue-400 hover:text-blue-300 underline">Privacy Policy</router-link> to understand how we collect, use, and protect your information.</p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">7. Intellectual Property</h2>
            <div class="space-y-4">
              <h3 class="text-lg font-medium text-blue-400 mb-2">Our Content</h3>
              <p>All content on CyberGame Hub, including but not limited to text, graphics, logos, and software, is owned by us or our licensors and is protected by copyright and other intellectual property laws.</p>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Games and Third-Party Content</h3>
              <p>Games and other third-party content are owned by their respective creators and are subject to their own terms and conditions.</p>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Copyright Infringement</h3>
              <p>If you believe any content on our site infringes your copyright, please contact us with detailed information about the alleged infringement.</p>
            </div>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">8. Account Management</h2>
            <div class="space-y-4">
              <h3 class="text-lg font-medium text-blue-400 mb-2">Account Security</h3>
              <p>You are responsible for maintaining the security of your account and password. Notify us immediately of any unauthorized use of your account.</p>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Account Termination</h3>
              <p>We may suspend or terminate your account if you violate these terms or engage in activities that harm our service or other users.</p>
            </div>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">9. Disclaimers and Limitations</h2>
            <div class="space-y-4">
              <h3 class="text-lg font-medium text-blue-400 mb-2">Service Availability</h3>
              <p>We provide our service "as is" and cannot guarantee uninterrupted access or error-free operation. We may temporarily suspend the service for maintenance or updates.</p>
              
              <h3 class="text-lg font-medium text-blue-400 mb-2">Limitation of Liability</h3>
              <p>To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our service.</p>
            </div>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">10. Age Restrictions</h2>
            <p>Our service is intended for users aged 13 and older. Users under 18 should have parental consent before using our service. We do not knowingly collect personal information from children under 13.</p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">11. Governing Law</h2>
            <p>These terms shall be governed by and construed in accordance with applicable international laws and regulations. Any disputes arising from these terms shall be resolved through appropriate legal channels.</p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">12. Changes to Terms</h2>
            <p>We reserve the right to modify these terms at any time. We will notify users of significant changes by posting a notice on our website. Your continued use of the service after such changes constitutes acceptance of the new terms.</p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-white mb-4">13. Contact Information</h2>
            <p>If you have any questions about these Terms of Service, please contact us:</p>
            <div class="mt-4 p-4 bg-gray-700 rounded-lg">
              <p><strong>Email:</strong> <a href="mailto:zhangshuaidoc@163.com" class="text-blue-400 hover:text-blue-300 underline">zhangshuaidoc@163.com</a></p>
              <p><strong>Website:</strong> <a href="https://cybergamehub.com" class="text-blue-400 hover:text-blue-300 underline">https://cybergamehub.com</a></p>
              <p><strong>Business Hours:</strong> 9:00-18:00 (UTC+8)</p>
            </div>
          </section>

          <div class="mt-8 pt-6 border-t border-gray-700">
            <p class="text-sm text-gray-400">
              <strong>Last Updated:</strong> {{ new Date().toLocaleDateString() }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Terms',
  mounted() {
    // Update page title
    document.title = 'Terms of Service - CyberGame Hub'
    
    // Track page view
    if (window.gtag) {
      window.gtag('config', 'G-45VN2LK7EM', {
        page_title: 'Terms of Service',
        page_location: window.location.href,
        page_path: '/terms'
      })
    }
  }
}
</script>

<style scoped>
.terms-of-service {
  min-height: 100vh;
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.terms-of-service h1 {
  background: linear-gradient(135deg, #60a5fa, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.terms-of-service h2 {
  border-bottom: 2px solid #374151;
  padding-bottom: 0.5rem;
}

.terms-of-service a {
  transition: color 0.2s ease-in-out;
}

@media (max-width: 768px) {
  .terms-of-service {
    padding-top: 1rem;
  }
  
  .terms-of-service h1 {
    font-size: 2rem;
  }
  
  .terms-of-service .bg-gray-800 {
    padding: 1.5rem;
  }
}
</style>
